import {DjPsycho} from "../DjPsycho";
import {DjPsychoGameAssets} from "../DjPsychoGameAssets";
import {TimelineLite} from "gsap";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {IDjPsychoGameData, IDjPsychoInitData} from "../server/IDjPsychoData";
import {DjPsychoSound} from "../sound/DjPsychoSound";

export enum DjPsychoSymbolStates {
    NORMAL,
    WIN,
    NO_WIN
}

export class DjPsychoSymbol extends PIXI.Sprite {
    private readonly _symbolCount: PIXI.BitmapText;
    private _symbolCountValue: number = 1;
    private readonly _symbolDouble: PIXI.Sprite;
    private readonly _noWin: PIXI.Sprite;
    private readonly _win: PIXI.Sprite;

    constructor(symbolName: string, initState: DjPsychoSymbolStates, soundId: number) {
        super();
        this._symbolName = symbolName;

        this._symbolDouble = new PIXI.Sprite();
        this._symbolDouble.visible = false;
        this._symbolDouble.anchor.set(0.5);

        this._symbolCountValue = 1;

        this._symbolCount = new PIXI.BitmapText("x" + this._symbolCountValue, {
            fontName: DjPsychoGameAssets.BITMAP_FONT_SYM_MULTIPLIER,
            fontSize: 40
        });
        this._symbolCount.visible = false;
        this._symbolCount.anchor = new PIXI.Point(1, 0);
        this._symbolCount.position.set(DjPsycho.symbolWidth / 2 - this._symbolCount.width / 8, DjPsycho.symbolHeight / 2 - this._symbolCount.height);

        this._noWin = new PIXI.Sprite(DjPsycho.assetLoader.getTextures("Sym_Tint")[0]);
        this._noWin.visible = false;
        this._noWin.anchor.set(0.5);

        this._win = new PIXI.Sprite(DjPsycho.assetLoader.getTextures("FX_Win/FX_Win_00006")[0]);
        this._win.visible = false;
        this._win.anchor.set(0.5);
        this._win.blendMode = PIXI.BLEND_MODES.ADD;

        this.addChild(this._symbolDouble, this._symbolCount, this._noWin, this._win);

        this.setState(initState);

        this.on("pointertap", () => this.onClick(soundId));
        this.enable(false);
    }

    private _symbolName: string;

    get symbolName(): string {
        return this._symbolName;
    }

    public setState(state: DjPsychoSymbolStates): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (state !== DjPsychoSymbolStates.NO_WIN && this._noWin.visible) {
            tl.to(this._noWin, {
                duration: 0.1,
                alpha: 0
            });
            tl.add(() => {
                this._noWin.visible = false;
                this._noWin.alpha = 0.7;
            });
        }

        switch (state) {
            case DjPsychoSymbolStates.WIN:
                this.texture = DjPsycho.assetLoader.getTextures(DjPsychoGameAssets.getSymbolStateTexturePath(this._symbolName))[0];

                tl.add(() => {
                    this._win.visible = true;
                });
                tl.to(this._win, {
                    duration: (15 / 30),
                    alpha: 0
                });
                tl.add(() => {
                    this._win.visible = false;
                    this._win.alpha = 1;
                });
                break;
            case DjPsychoSymbolStates.NO_WIN:
                tl.add(() => {
                    this._noWin.alpha = 0;
                    this._noWin.visible = true;
                });
                tl.to(this._noWin, {
                    duration: 0.3,
                    alpha: 0.7
                });
                break;
            default:
                this.texture = DjPsycho.assetLoader.getTextures(DjPsychoGameAssets.getSymbolStateTexturePath(this._symbolName))[0];
        }

        return tl;
    }

    public updateTexture(newName: string): void {
        // #127
        if (this._symbolName === "W" && this.alpha) {
            return;
        }

        this._symbolDouble.alpha = 1;
        this._symbolName = newName;

        // #53 fixing crossed wild bug
        if (newName === "W") {
            this._symbolDouble.visible = false;
        }

        this.setState(DjPsychoSymbolStates.NORMAL);
    }

    public disappear(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        tl.to(this, {
            duration: 0.3,
            alpha: 0
        });

        tl.add(() => {
            this._symbolDouble.visible = false;

            this._symbolCountValue = 1;

            this._symbolCount.visible = false;
            this._symbolCount.text = "x" + this._symbolCountValue;

            this._noWin.visible = false;
        });

        return tl;
    }

    public appear(reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (this.alpha) {
            return tl;
        }

        if (DjPsycho.model.gameData.mode.indexOf("AVALANCHE") > -1) {
            tl.add(DjPsycho.winView.playRevealAnimation(reelId, rowId));
        } else {
            tl.add(() => {
                DjPsycho.winView.stopAppearAnimation(reelId, rowId);
            });
        }
        tl.fromTo(this, {
            alpha: 0
        }, {
            duration: 11 / 30,
            alpha: 1
        }, 0);

        return tl;
    }

    public setSymbolCount(): void {
        this._symbolCountValue *= 2;

        this._symbolCount.text = "x" + this._symbolCountValue;
        this._symbolCount.position.set(DjPsycho.symbolWidth / 2 - this._symbolCount.width / 8, DjPsycho.symbolHeight / 2 - this._symbolCount.height);
        if (this._symbolCountValue > 1) {
            this._symbolCount.visible = true;
        }
    }

    public doSymbolDouble(newName: string, reelId: number, symId: number, calledFromXM: boolean): TimelineLite {
        const duration: number = (10 / 30),
            tl: TimelineLite = new TimelineLite(),
            data: IDjPsychoGameData | IDjPsychoInitData = DjPsycho.model.data,
            reels: string[][] = data.reels,
            evaluatedArea: string[][] = data.evaluatedArea || [[], [], [], []];

        // #132 => got a test case when solving #132
        if (this._symbolDouble.visible && this._symbolDouble.alpha) {
            return tl;
        }

        if (newName[1] === this._symbolName[1] || DjPsycho.WILD_SYMBOL_NAMES.indexOf(this._symbolName) > -1) {
            const wiggles = ArrayHelper.shuffle([DjPsychoGameAssets.shakeWiggle, DjPsychoGameAssets.shakeWiggle2]),
                x: string = Math.random() > 0.5 ? "-=2" : "+=2",
                y: string = Math.random() > 0.5 ? "-=1" : "+=1";

            tl.to(this, {
                duration: duration,
                x: x,
                ease: wiggles[0]
            });
            tl.to(this, {
                duration: duration,
                y: y,
                ease: wiggles[1]
            }, "<");
            tl.to(this, {
                duration: duration,
                rotation: 0.1,
                ease: wiggles[1]
            }, "<");

            return tl.reverse();
        }

        tl.add(() => {
            this._symbolDouble.texture = DjPsycho.assetLoader.getTextures("SYM_Double/" + newName)[0];
            this._symbolDouble.alpha = 0;
            this._symbolDouble.visible = true;
        });

        if ((reels[reelId][symId] === "xM" && evaluatedArea[reelId][symId] !== "xM" && !DjPsycho.model.isRestore) || (!calledFromXM && reels[reelId][symId] === "xZ")) {
            this._symbolDouble.alpha = 0;
        } else {
            tl.to(this._symbolDouble, {
                duration: duration,
                alpha: 1
            });
        }

        return tl;
    }

    public enable(flag: boolean): void {
        this.interactive = flag;
        this.buttonMode = flag;
    }

    private onClick(soundId: number): void {
        const tl: TimelineLite = new TimelineLite(),
            duration: number = (3 / 30);

        if (this._symbolDouble.visible) {
            return;
        }

        if (this._symbolName[0] === "M" || this._symbolName[0] === "L") {
            tl.add(() => {
                this._noWin.alpha = 0.2;
                this._noWin.visible = true;

                this.scale.set(0.95);
                DjPsycho.soundPlayer.playSound(DjPsychoSound.PAD_SOUND_NAME + soundId);
            });
            tl.add(() => {
                this._noWin.visible = false;

                this.scale.set(1);
            }, duration);
        }
    }
}